import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { FaUtensils } from "react-icons/fa";
import RecipeBanner from "./Banner/MenuBanner";
import { api, endpoints } from "../../utils/axiosInstance";

const MenuPage = () => {
  const location = useLocation();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeType, setActiveType] = useState("Tất cả");
  const [typeList, setTypeList] = useState(["Tất cả"]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    limit: 6,
  });
  const [sortBy, setSortBy] = useState("-createdAt");

  useEffect(() => {
    if (location.state?.selectedType) {
      setActiveType(location.state.selectedType);
    }
  }, [location.state]);

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: pagination.limit,
        skip: (currentPage - 1) * pagination.limit,
        sort: sortBy,
      };

      if (searchKeyword.trim()) params.search = searchKeyword.trim();

      if (activeType !== "Tất cả") {
        const englishTypes = {
          "Bữa sáng": "breakfast",
          "Bữa trưa": "lunch",
          "Bữa tối": "dinner",
          "Ăn vặt": "snack",
        };
        params.type = englishTypes[activeType] || activeType;
      }

      const response = await api.get(endpoints.menus.list, { params });

      setMenus(response.data || []);
      setError("");

      const paginationData = response.pagination || {};
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total || 0,
        totalPages: paginationData.totalPages || 0,
      }));

      if (Array.isArray(response.types)) {
        const vietnameseTypes = {
          breakfast: "Bữa sáng",
          lunch: "Bữa trưa",
          dinner: "Bữa tối",
          snack: "Ăn vặt",
        };
        const translatedTypes = response.types.map(
          (type) => vietnameseTypes[type.toLowerCase()] || capitalize(type)
        );
        setTypeList(["Tất cả", ...translatedTypes]);
      }
    } catch (err) {
      setMenus([]);
      setError(err.response?.message || "Không tìm thấy thực đơn phù hợp");
      setPagination((prev) => ({
        ...prev,
        total: 0,
        totalPages: 0,
      }));
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, currentPage, sortBy, searchKeyword, activeType]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchMenus();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [fetchMenus]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setCurrentPage(1);
  };

  const handleTypeChange = (type) => {
    setActiveType(type);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  return (
    <PageContainer>
      <RecipeBanner />
      <Title>🍳 Gợi Ý Thực Đơn</Title>

      <SearchContainer>
        <SearchBar
          type="text"
          placeholder="Tìm kiếm thực đơn..."
          value={searchKeyword}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="-createdAt">Mới nhất</option>
          <option value="createdAt">Cũ nhất</option>
          <option value="-totalCalories">Calories cao - thấp</option>
          <option value="totalCalories">Calories thấp - cao</option>
          <option value="name">Tên A-Z</option>
          <option value="-name">Tên Z-A</option>
        </SortSelect>
      </SearchContainer>

      <FilterTitle>
        <span>
          <FaUtensils />
        </span>{" "}
        Bữa ăn
      </FilterTitle>
      <TagList>
        {typeList.map((type) => (
          <TagButton
            key={type}
            active={activeType === type}
            onClick={() => handleTypeChange(type)}
          >
            {type}
          </TagButton>
        ))}
      </TagList>

      {loading ? (
        <LoadingWrapper>
          <LoadingSpinner />
        </LoadingWrapper>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <>
          <MenuGrid>
            {menus.map((menu) => (
              <MenuCard key={menu._id} as={Link} to={`/menus/${menu.slug}`}>
                <ImageWrapper>
                  <img src={menu.imageUrl} alt={menu.name} />
                  <StatsContainer>
                    <StatItem>
                      <StatLabel>Calories</StatLabel>
                      <StatValue>{menu.totalCalories || "N/A"} kcal</StatValue>
                    </StatItem>
                    <StatItem>
                      <StatLabel>Khẩu phần</StatLabel>
                      <StatValue>{menu.serves || "N/A"} người</StatValue>
                    </StatItem>
                  </StatsContainer>
                </ImageWrapper>
                <MenuInfo>
                  <MenuTitle>{menu.name}</MenuTitle>
                  <MenuDesc>{menu.description}</MenuDesc>
                  <MetaRow>
                    <TimeDifficulty>
                      <span>{menu.time}</span>
                      <span>{menu.difficulty}</span>
                    </TimeDifficulty>
                  </MetaRow>
                </MenuInfo>
              </MenuCard>
            ))}
          </MenuGrid>

          {pagination.totalPages > 1 && (
            <PaginationContainer>
              {currentPage > 1 && (
                <PageButton onClick={() => handlePageChange(currentPage - 1)}>
                  Prev
                </PageButton>
              )}
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .filter((pageNum) => {
                  return (
                    pageNum === 1 ||
                    pageNum === pagination.totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  );
                })
                .map((pageNum, index, array) => {
                  if (index > 0 && array[index - 1] !== pageNum - 1) {
                    return [
                      <PageEllipsis key={`ellipsis-${pageNum}`}>
                        ...
                      </PageEllipsis>,
                      <PageButton
                        key={pageNum}
                        active={currentPage === pageNum}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </PageButton>,
                    ];
                  }
                  return (
                    <PageButton
                      key={pageNum}
                      active={currentPage === pageNum}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </PageButton>
                  );
                })}
              {currentPage < pagination.totalPages && (
                <PageButton onClick={() => handlePageChange(currentPage + 1)}>
                  Next
                </PageButton>
              )}
            </PaginationContainer>
          )}
        </>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  justify-content: center;
  align-items: center;
`;

const SearchBar = styled.input`
  padding: 0.75rem 1.25rem;
  width: 400px;
  font-size: 1rem;
  border-radius: 30px;
  border: 1.5px solid #ccc;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: rgb(255, 159, 28); // Thay đổi màu focus
    box-shadow: 0 0 0 2px rgba(255, 159, 28, 0.1); // Thay đổi bóng focus
  }
`;

const SortSelect = styled.select`
  padding: 0.75rem 1.25rem;
  border-radius: 30px;
  border: 1.5px solid #ccc;
  outline: none;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    border-color: rgb(255, 159, 28); // Thay đổi màu focus
  }

  &:hover {
    border-color: rgb(255, 159, 28); // Thay đổi màu hover
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid rgb(255, 159, 28); // Thay đổi màu spinner
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: rgb(255, 159, 28); // Thay đổi màu lỗi
  padding: 20px;
  font-size: 1.1rem;
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MenuCard = styled(Link)`
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 220px;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  ${MenuCard}:hover img {
    transform: scale(1.05);
  }
`;

const StatsContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.6);
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  z-index: 1;
`;

const StatLabel = styled.span`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const StatValue = styled.span`
  font-size: 1rem;
  color: white;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const MenuInfo = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MenuTitle = styled.h2`
  font-size: 1.3rem;
  margin: 0;
  color: #333;
  font-weight: 700;
  line-height: 1.4;
`;

const MenuDesc = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  color: #666;
`;

const TimeDifficulty = styled.div`
  display: flex;
  gap: 1rem;
  font-weight: 500;
`;

const FilterTitle = styled.h5`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  gap: 0.5rem;
  cursor: pointer;

  span {
    font-size: 1.2rem;
    color: rgb(255, 159, 28); // Thay đổi màu span
  }

  &:hover {
    opacity: 0.8;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TagButton = styled.button`
  background: ${({ active }) =>
    active ? "rgb(255, 159, 28)" : "#f5f2ec"}; // Thay đổi màu active
  color: ${({ active }) => (active ? "#000" : "#000")};
  border: 1.5px solid
    ${({ active }) => (active ? "rgb(255, 159, 28)" : "#c9cfc9")}; // Thay đổi viền active
  padding: 0.5rem 1.4rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: ${({ active }) =>
    active
      ? "0 0 0 1.5px rgb(255, 159, 28)"
      : "0 0 0 1px #c9cfc9"}; // Thay đổi bóng active

  &:hover {
    background: rgb(255, 174, 66); // Thay đổi màu hover
    color: #000;
    border-color: rgb(255, 174, 66); // Thay đổi viền hover
  }

  &:disabled {
    background: #f0eee9;
    color: #999;
    border-color: #ddd;
    cursor: not-allowed;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  gap: 0.5rem;
`;

const PageButton = styled.button`
  background: ${({ active }) =>
    active ? "rgb(255, 159, 28)" : "#fff"}; // Thay đổi màu active
  color: #000;
  border: 1px solid #ccc;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgb(255, 174, 66); // Thay đổi màu hover
  }
`;

const PageEllipsis = styled.span`
  padding: 0.5rem 1rem;
  color: #666;
`;

export default MenuPage;
